/**
 * Bridge between Apple Contacts and Meridian's contact state.
 *
 * On iOS, this hook manages the full lifecycle: request permission,
 * fetch all contacts, observe changes from the native store, and
 * write edits back to Apple. On desktop/web it returns an empty
 * array so the app compiles and runs for development.
 *
 * Write-back uses a writingRef guard to prevent the change observer
 * from re-fetching contacts immediately after our own save. The guard
 * stays up for 1 second after each write to cover the async delay
 * between CNContactStore.execute() and the notification firing.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { invoke, addPluginListener, type PluginListener } from "@tauri-apps/api/core";
import type { Contact } from "@/lib/data";
import { appleContactToContact, contactToAppleContact } from "@/lib/appleContact";
import type { AppleContact } from "@/lib/appleContact";

type PermissionState = "granted" | "denied" | "restricted" | "notDetermined" | "limited";

interface UseContactsResult {
  contacts: Contact[];
  loading: boolean;
  permissionStatus: PermissionState | null;
  refetch: () => Promise<void>;
  updateAppleContact: (contact: Contact) => Promise<void>;
  createAppleContact: (contact: Contact) => Promise<Contact>;
  deleteAppleContact: (identifier: string) => Promise<void>;
}

// Tauri injects this global when running inside a native shell
const isNative = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export function useContacts(): UseContactsResult {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionStatus, setPermissionStatus] = useState<PermissionState | null>(null);
  const fetchingRef = useRef(false);
  const writingRef = useRef(false);

  /**
   * Pull every contact from Apple that has at least one phone number.
   * The Swift plugin returns either a bare array or { contacts: [...] }
   * depending on how Tauri wraps the response, so we handle both shapes.
   */
  const fetchContacts = useCallback(async () => {
    if (!isNative) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const raw = await invoke<unknown>("plugin:contacts|fetch_all_contacts");

      let appleContacts: AppleContact[];
      if (Array.isArray(raw)) {
        appleContacts = raw as AppleContact[];
      } else if (raw && typeof raw === "object" && "contacts" in (raw as object)) {
        appleContacts = (raw as { contacts: AppleContact[] }).contacts;
      } else {
        return;
      }

      const mapped = appleContacts.map(appleContactToContact);
      setContacts(mapped);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  /**
   * Check current permission state and request access if not yet determined.
   * Only fetches contacts after permission is confirmed as granted or limited.
   */
  const requestAccess = useCallback(async () => {
    if (!isNative) {
      setLoading(false);
      return;
    }

    try {
      const status = await invoke<{ status: PermissionState }>(
        "plugin:contacts|check_permissions"
      );
      setPermissionStatus(status.status);

      if (status.status === "notDetermined") {
        const result = await invoke<{ status: PermissionState }>(
          "plugin:contacts|request_permissions"
        );
        setPermissionStatus(result.status);
        if (result.status === "granted" || result.status === "limited") {
          await fetchContacts();
        } else {
          setLoading(false);
        }
      } else if (status.status === "granted" || status.status === "limited") {
        await fetchContacts();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Permission error:", error);
      setLoading(false);
    }
  }, [fetchContacts]);

  /**
   * On mount: request access, start the native change observer, and listen
   * for change events. The observer fires CNContactStoreDidChangeNotification
   * whenever any app modifies Apple Contacts. We debounce by 500ms and skip
   * events caused by our own writes (writingRef guard).
   */
  useEffect(() => {
    requestAccess();

    if (!isNative) return;

    let listener: PluginListener | undefined;
    let cancelled = false;

    invoke("plugin:contacts|start_observing").catch(console.error);

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    addPluginListener("contacts", "contacts-changed", () => {
      if (writingRef.current) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchContacts(), 500);
    }).then((l) => {
      if (cancelled) {
        l.unregister();
      } else {
        listener = l;
      }
    }).catch((err) => {
      console.warn("contacts listener failed:", err);
    });

    return () => {
      cancelled = true;
      listener?.unregister();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [requestAccess, fetchContacts]);

  /**
   * Write a Meridian contact back to Apple Contacts.
   * Converts to the Apple format, invokes the Swift plugin, and holds
   * writingRef high for 1 second so the change observer ignores our
   * own notification.
   */
  const updateAppleContact = useCallback(
    async (contact: Contact) => {
      if (!isNative || !contact.appleContactId) return;
      try {
        writingRef.current = true;
        const appleContact = contactToAppleContact(contact);
        await invoke("plugin:contacts|update_contact", { contact: appleContact });
      } catch (error) {
        console.error("Failed to sync contact to Apple:", error);
      } finally {
        setTimeout(() => { writingRef.current = false; }, 1000);
      }
    },
    []
  );

  /** Create a new contact in Apple Contacts and return it with the assigned identifier. */
  const createAppleContact = useCallback(
    async (contact: Contact): Promise<Contact> => {
      if (!isNative) return contact;
      const appleContact = contactToAppleContact(contact);
      const created = await invoke<AppleContact>("plugin:contacts|create_contact", {
        contact: appleContact,
      });
      return appleContactToContact(created);
    },
    []
  );

  /** Remove a contact from Apple Contacts by its Apple identifier. */
  const deleteAppleContact = useCallback(
    async (identifier: string) => {
      if (!isNative) return;
      await invoke("plugin:contacts|delete_contact", { identifier });
    },
    []
  );

  return {
    contacts,
    loading,
    permissionStatus,
    refetch: fetchContacts,
    updateAppleContact,
    createAppleContact,
    deleteAppleContact,
  };
}
